import React, { createContext, useContext, useCallback, useState, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import useSpaceStore from '../../stores/spaceStore';
import { AnyWindowEntity } from '../../types/windows';
import { getWindowTitle as getWindowTitleFromUtils } from '../Space/utils/windowUtils';
import { createFileLogger } from '../../utils/logger';
const log = createFileLogger();

interface SystemContextType {
  user: any;
  isLoading: boolean;
  activeWindow: AnyWindowEntity | null;
  getWindowTitle: (window: AnyWindowEntity) => string;
  pageType: 'home' | 'space' | 'standalone' | null;
  spaceId: string | null;
  setPageContext: (pageType: 'home' | 'space' | 'standalone', spaceId?: string, appId?: string) => void;
}

// Persist the context object across Vite HMR hot-reloads.
// When SystemContext.tsx is hot-reloaded, createContext() would produce a new object,
// but React.lazy-cached modules (like SpacePageWrapper) still hold the old reference.
// Storing the context on globalThis ensures provider and consumer always use the same instance.
const CONTEXT_KEY = Symbol.for('__river_system_context__');
const SystemContext: React.Context<SystemContextType | undefined> =
  (globalThis as any)[CONTEXT_KEY] ??
  ((globalThis as any)[CONTEXT_KEY] = createContext<SystemContextType | undefined>(undefined));

export const useSystemContext = () => {
  const context = useContext(SystemContext);
  if (!context) {
    throw new Error('useSystemContext must be used within a SystemContextProvider');
  }
  return context;
};

interface SystemContextProviderProps {
  children: React.ReactNode;
}

export const SystemContextProvider: React.FC<SystemContextProviderProps> = ({ children }) => {
  const { user, isLoading: authIsLoading } = useAuth();
  
  // 🔥 CRITICAL FIX: Use selectors instead of subscribing to entire store
  // This prevents re-rendering App.tsx when unrelated store fields change
  const activeSpace = useSpaceStore((s) => s.activeSpace);
  const initializationStatus = useSpaceStore((s) => s.initializationStatus);
  const activeWindowId = useSpaceStore((s) => s.activeWindowId);
  
  const [pageType, setPageType] = useState<'home' | 'space' | 'standalone' | null>(null);
  const [spaceId, setSpaceId] = useState<string | null>(null);
  const [isLoading] = useState(false);  // Keep for backwards compatibility
  
  // 🎯 PERFORMANCE: Get active window without subscribing to all windows
  // Only re-render if activeWindowId changes, not when other windows change
  const activeWindow = useMemo<AnyWindowEntity | null>(() => {
    if (!activeWindowId) return null;
    // Read from store without subscribing
    const windows = useSpaceStore.getState().windows;
    return windows[activeWindowId] || null;
  }, [activeWindowId]);

  // User is now handled by useAuth hook - no manual fallback needed!

  // Filter activeWindow based on page context (only show for initialized spaces)
  const contextActiveWindow: AnyWindowEntity | null = useMemo(() => {
    return pageType === 'space' && 
      activeSpace && 
      initializationStatus === 'initialized' 
      ? activeWindow 
      : null;
  }, [pageType, activeSpace, initializationStatus, activeWindow]);

  // Window title helper function (stable - no dependencies)
  const getWindowTitle = useCallback((window: AnyWindowEntity) => {
    if (window.type === 'window') {
      return getWindowTitleFromUtils(window);
    }
    return 'Untitled';
  }, []);

  // Set page context (stable callback)
  const setPageContext = useCallback((newPageType: 'home' | 'space' | 'standalone', newSpaceId?: string, appId?: string) => {
    setPageType(newPageType);
    setSpaceId(newSpaceId || null);
    try {
      // Get method directly instead of from closure
      useSpaceStore.getState().setPageContext(newPageType, newSpaceId, appId);
    } catch {}
    import('../../utils/perfInstrumentation').then(({ perf }) => perf.increment('setPageContextCount')).catch(() => {});
  }, []);

  // 🔥 CRITICAL FIX: Memoize context value to prevent re-render cascade
  // Without this, every render creates a new object reference, causing ALL consumers to re-render
  const value: SystemContextType = useMemo(() => ({
    user,
    isLoading,
    activeWindow: contextActiveWindow,
    getWindowTitle,
    pageType,
    spaceId,
    setPageContext,
  }), [user, isLoading, contextActiveWindow, getWindowTitle, pageType, spaceId, setPageContext]);

  return <SystemContext.Provider value={value}>{children}</SystemContext.Provider>;
}; 